<lib-sales-insights-card
  [cardTitle]="config?.title"
  [subtitle]="subtitle"
  [loading]="false"
  [error]="error"
  [empty]="false"
>
  @if (canBook) {
    <lib-sales-insights-book-activity
      cardActions
      labelKey="SALES_INSIGHTS_NEW_ACTIVITY"
      icon="fa-solid fa-plus"
    ></lib-sales-insights-book-activity>
  }
  <div cardToolbar class="si-activities__toolbar">
    <label class="si-activities__search">
      <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      <input
        type="search"
        class="form-control"
        [value]="search"
        [placeholder]="'SALES_INSIGHTS_ACTIVITY_SEARCH' | translate"
        [attr.aria-label]="'SALES_INSIGHTS_ACTIVITY_SEARCH' | translate"
        (input)="onSearch($any($event.target).value)"
      />
    </label>
    <div class="si-activities__select">
      <lib-gung-input-multi-select
        [optionsList]="typeOptions"
        [singleSelection]="true"
        [showEmptyIds]="true"
        [showId]="false"
        previewField="name"
        (selectedValues)="selectFilter('type', $event)"
      ></lib-gung-input-multi-select>
    </div>
    <div class="si-activities__select">
      <lib-gung-input-multi-select
        [optionsList]="statusOptions"
        [singleSelection]="true"
        [showEmptyIds]="true"
        [showId]="false"
        previewField="name"
        (selectedValues)="selectFilter('status', $event)"
      ></lib-gung-input-multi-select>
    </div>
    @if (refreshing) {
      <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
    }
  </div>

  @if (showBulkBar) {
    <div
      cardToolbar
      class="si-activities__bulk"
      role="region"
      [attr.aria-label]="'SALES_INSIGHTS_ACTIVITY_SELECTED' | translate: { count: selected.size }"
    >
      @if (selected.size) {
        <span class="si-activities__bulk-count">
          {{ 'SALES_INSIGHTS_ACTIVITY_SELECTED' | translate: { count: selected.size } }}
        </span>
        <button type="button" class="btn btn-sm si-activities__link" (click)="clearSelection()">
          {{ 'SALES_INSIGHTS_ACTIVITY_CLEAR_SELECTION' | translate }}
        </button>
        <span class="si-activities__bulk-spacer"></span>
        @if (moving) {
          <div class="si-activities__move">
            <lib-gung-date-picker
              [suppressLabel]="true"
              [currentselectedDate]="moveDay || ''"
              container="body"
              (selectedDate)="setMoveDay($event)"
            ></lib-gung-date-picker>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              [disabled]="!moveDay || bulkBusy"
              (click)="moveSelected()"
            >
              {{ 'SALES_INSIGHTS_ACTIVITY_MOVE' | translate }}
            </button>
            <button type="button" class="btn btn-sm si-activities__button" (click)="clearSelection()">
              {{ 'CANCEL' | translate }}
            </button>
          </div>
        } @else {
          <button type="button" class="btn btn-sm si-activities__button" [disabled]="bulkBusy" (click)="markDone()">
            <i class="fa-solid fa-check"></i>{{ 'SALES_INSIGHTS_ACTIVITY_MARK_DONE' | translate }}
          </button>
          <button type="button" class="btn btn-sm si-activities__button" [disabled]="bulkBusy" (click)="moving = true">
            <i class="fa-regular fa-calendar"></i>{{ 'SALES_INSIGHTS_ACTIVITY_MOVE_DATE' | translate }}
          </button>
        }
        @if (bulkBusy) {
          <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
        }
      } @else if (bulkResult) {
        <span class="si-activities__bulk-count">
          <i class="fa-solid fa-check me-1 text-success"></i>
          {{ 'SALES_INSIGHTS_ACTIVITY_SAVED' | translate: { count: bulkResult.saved } }}
          @if (bulkResult.failed) {
            <span class="text-danger ms-2">{{
              'SALES_INSIGHTS_ACTIVITY_FAILED' | translate: { count: bulkResult.failed }
            }}</span>
          }
        </span>
      }
      @if (bulkError) {
        <span class="si-activities__error">
          <i class="fa-solid fa-triangle-exclamation me-1"></i>{{ bulkError | translate }}
        </span>
      }
    </div>
  }

  @if (loading) {
    <div class="si-activities si-activities--loading" aria-busy="true">
      @for (row of skeletonRows; track row) {
        <div class="si-activities__skeleton">
          <span class="si-activities__bone si-activities__bone--round"></span>
          <span class="si-activities__bone si-activities__bone--icon"></span>
          <span class="si-activities__bone si-activities__bone--text"></span>
          <span class="si-activities__bone si-activities__bone--short"></span>
          <span class="si-activities__bone si-activities__bone--short"></span>
        </div>
      }
    </div>
  } @else if (!sections.length) {
    <div class="si-activities__empty">
      <span class="si-activities__empty-icon" aria-hidden="true">
        <i [class]="filtered ? 'fa-solid fa-filter-circle-xmark' : 'fa-regular fa-calendar-check'"></i>
      </span>
      <p class="si-activities__empty-title">
        {{ (filtered ? 'SALES_INSIGHTS_ACTIVITY_EMPTY_FILTERED' : 'SALES_INSIGHTS_ACTIVITY_EMPTY') | translate }}
      </p>
      @if (filtered) {
        <button type="button" class="btn btn-sm si-activities__button" (click)="clearFilters()">
          {{ 'SALES_INSIGHTS_ACTIVITY_CLEAR_FILTERS' | translate }}
        </button>
      } @else if (canBook) {
        <lib-sales-insights-book-activity
          labelKey="SALES_INSIGHTS_NEW_ACTIVITY"
          icon="fa-solid fa-plus"
        ></lib-sales-insights-book-activity>
      }
    </div>
  } @else {
    <div class="si-activities" [class.si-activities--bulk]="bulkActions">
      <table class="table mb-0">
        <thead>
          <tr>
            @if (bulkActions) {
              <th scope="col" class="si-activities__check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  [checked]="allSelected"
                  [attr.aria-label]="'SALES_INSIGHTS_ACTIVITY_SELECT_ALL' | translate"
                  (change)="toggleAll()"
                />
              </th>
            }
            <th scope="col" class="si-activities__done-col">
              <span class="visually-hidden">{{ 'SALES_INSIGHTS_ACTIVITY_COL_STATUS' | translate }}</span>
            </th>
            <th scope="col" class="si-activities__activity-col">{{ 'SALES_INSIGHTS_ACTIVITY_COL_ACTIVITY' | translate }}</th>
            @if (columns.customer) {
              <th scope="col" class="si-activities__customer-col">{{ 'SALES_INSIGHTS_HEALTH_COL_CUSTOMER' | translate }}</th>
            }
            <th scope="col" class="si-activities__date-col">{{ 'SALES_INSIGHTS_ACTIVITY_COL_DATE' | translate }}</th>
            @if (columns.responsible) {
              <th scope="col" class="si-activities__responsible-col">{{ 'SALES_INSIGHTS_ACTIVITY_COL_RESPONSIBLE' | translate }}</th>
            }
            @if (rowActions) {
              <th scope="col" class="si-activities__actions-col">
                <span class="visually-hidden">{{ 'SALES_INSIGHTS_ACTIVITY_COL_ACTIONS' | translate }}</span>
              </th>
            }
          </tr>
        </thead>
        @for (section of sections; track section.group) {
          <tbody [class]="'si-activities__section si-activities__section--' + section.group">
            <tr class="si-activities__band">
              <th [attr.colspan]="columnCount" scope="rowgroup">
                <button
                  type="button"
                  class="si-activities__band-toggle"
                  [class.si-activities__band-toggle--collapsed]="collapsed.has(section.group)"
                  [attr.aria-expanded]="!collapsed.has(section.group)"
                  (click)="toggleGroup(section.group)"
                >
                  <i class="fa-solid fa-chevron-down si-activities__chevron" aria-hidden="true"></i>
                  <i [class]="section.icon + ' si-activities__band-icon'" aria-hidden="true"></i>
                  <span class="si-activities__band-label">{{ section.labelKey | translate }}</span>
                  <span class="si-activities__band-count">{{ section.count }}</span>
                  @if (section.shown) {
                    <span class="si-activities__shown">{{
                      'SALES_INSIGHTS_ACTIVITY_SHOWN' | translate: { count: section.shown }
                    }}</span>
                  }
                </button>
              </th>
            </tr>
            @if (!collapsed.has(section.group)) {
              @for (view of section.rows; track view.row.id) {
                <tr
                  class="si-activities__row"
                  [class.si-activities__row--picked]="selected.has(view.row.id)"
                  [class.si-activities__row--done]="view.row.done"
                  [class.si-activities__row--saving]="saving.has(view.row.id)"
                  [style.--si-type-rgb]="view.typeRgb"
                  tabindex="0"
                  (click)="open(view)"
                  (keydown.enter)="open(view)"
                >
                  @if (bulkActions) {
                    <td class="si-activities__check" (click)="$event.stopPropagation()">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        [checked]="selected.has(view.row.id)"
                        [attr.aria-label]="view.row.customerName"
                        (click)="$event.stopPropagation()"
                        (change)="toggle(view.row.id, $event)"
                      />
                    </td>
                  }
                  <td class="si-activities__done-col">
                    <button
                      type="button"
                      class="si-activities__done"
                      [class.si-activities__done--on]="view.row.done"
                      [disabled]="saving.has(view.row.id)"
                      [attr.aria-pressed]="view.row.done"
                      [attr.aria-label]="
                        (view.row.done ? 'SALES_INSIGHTS_ACTIVITY_REOPEN' : 'SALES_INSIGHTS_ACTIVITY_MARK_ONE_DONE')
                          | translate
                      "
                      [ngbTooltip]="view.statusName"
                      container="body"
                      (click)="toggleDone(view, $event)"
                      (keydown.enter)="$event.stopPropagation()"
                    >
                      <i class="fa-solid fa-check" aria-hidden="true"></i>
                    </button>
                  </td>
                  <td class="si-activities__activity">
                    <div class="si-activities__subject">
                      <span class="si-activities__type-icon" aria-hidden="true"><i [class]="view.typeIcon"></i></span>
                      <span class="si-activities__subject-text">
                        <span class="si-activities__type">{{ view.typeName }}</span>
                        @if (view.row.comment) {
                          <span class="si-activities__comment" [title]="view.row.comment">{{ view.row.comment }}</span>
                        }
                      </span>
                    </div>
                  </td>
                  @if (columns.customer) {
                    <td class="si-activities__customer">
                      <span class="si-activities__name">{{ view.row.customerName || view.row.customerId }}</span>
                      <span class="si-activities__meta">
                        @if (view.row.customerName && view.row.customerId) {
                          <span class="si-activities__number">{{ view.row.customerId }}</span>
                        }
                        @if (view.row.city) {
                          <span class="si-activities__city"
                            ><i class="fa-solid fa-location-dot" aria-hidden="true"></i>{{ view.row.city }}</span
                          >
                        }
                      </span>
                    </td>
                  }
                  <td [class]="'si-activities__due si-activities__due--' + view.due.kind">
                    <span class="si-activities__due-label">{{ view.due.label }}</span>
                    @if (view.due.sub) {
                      <span class="si-activities__due-sub">{{ view.due.sub }}</span>
                    }
                  </td>
                  @if (columns.responsible) {
                    <td class="si-activities__responsible">
                      @if (view.responsibleName) {
                        <span
                          class="si-activities__person"
                          [style.--si-person-rgb]="view.responsibleRgb"
                          [title]="view.responsibleName"
                        >
                          <span
                            class="si-activities__avatar"
                            [attr.aria-hidden]="view.responsibleInitials !== view.responsibleName"
                            >{{ view.responsibleInitials }}</span
                          >
                          @if (view.responsibleInitials !== view.responsibleName) {
                            <span class="si-activities__person-name">{{ view.responsibleName }}</span>
                          }
                        </span>
                      }
                    </td>
                  }
                  @if (rowActions) {
                    <td class="si-activities__actions-col">
                      <div class="si-activities__actions">
                        @if (!view.row.done) {
                          <button
                            type="button"
                            class="si-activities__action"
                            [disabled]="saving.has(view.row.id)"
                            [ngbTooltip]="'SALES_INSIGHTS_ACTIVITY_MARK_ONE_DONE' | translate"
                            [attr.aria-label]="'SALES_INSIGHTS_ACTIVITY_MARK_ONE_DONE' | translate"
                            container="body"
                            (click)="toggleDone(view, $event)"
                            (keydown.enter)="$event.stopPropagation()"
                          >
                            <i class="fa-solid fa-check"></i>
                          </button>
                        }
                        <button
                          type="button"
                          class="si-activities__action"
                          [ngbTooltip]="'SALES_INSIGHTS_ACTIVITY_MOVE_DATE' | translate"
                          [attr.aria-label]="'SALES_INSIGHTS_ACTIVITY_MOVE_DATE' | translate"
                          container="body"
                          (click)="moveOne(view, $event)"
                          (keydown.enter)="$event.stopPropagation()"
                        >
                          <i class="fa-regular fa-calendar"></i>
                        </button>
                        @if (view.row.customerId) {
                          <button
                            type="button"
                            class="si-activities__action"
                            [ngbTooltip]="'SALES_INSIGHTS_ACTIVITY_OPEN_CUSTOMER' | translate"
                            [attr.aria-label]="'SALES_INSIGHTS_ACTIVITY_OPEN_CUSTOMER' | translate"
                            container="body"
                            (click)="openCustomer(view, $event)"
                            (keydown.enter)="$event.stopPropagation()"
                          >
                            <i class="fa-solid fa-arrow-up-right-from-square"></i>
                          </button>
                        }
                      </div>
                    </td>
                  }
                </tr>
              }
            }
          </tbody>
        }
      </table>
    </div>
  }
</lib-sales-insights-card>
